import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import consoleApi, { clearSession, getSession } from "./consoleApi";
import { ActivityIcon, BuildingIcon, LogoutIcon } from "./icons";

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-400 to-violet-600 text-sm font-extrabold text-white shadow-lg shadow-indigo-900/40">
        R
      </span>
      <div className="leading-tight">
        <p className="text-sm font-bold text-white">RiskFlow</p>
        <p className="text-[11px] font-medium text-indigo-200/70">Staff console</p>
      </div>
    </div>
  );
}

function NavItem({ to, end, icon: IconComponent, label, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <IconComponent className={`h-4 w-4 ${isActive ? "text-indigo-300" : ""}`} />
          <span className="flex-1">{label}</span>
          {badge > 0 && (
            <span className="rounded-full bg-amber-400 px-1.5 py-px text-[11px] font-bold tabular-nums text-amber-950" title={`${badge} waiting for review`}>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function ConsoleLayout() {
  const navigate = useNavigate();
  const session = getSession();
  const [overview, setOverview] = useState(null);

  // Shared with pages through the outlet, so the pending badge updates after an approval
  const refreshOverview = useCallback(() => {
    consoleApi.get("/overview")
      .then((res) => setOverview(res.data.data))
      .catch(() => setOverview((prev) => prev || {}));
  }, []);

  useEffect(() => { refreshOverview(); }, [refreshOverview]);

  const signOut = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  const pending = overview?.tenants?.pending || 0;
  const nav = (
    <>
      <NavItem to="/" end icon={BuildingIcon} label="Tenants" badge={pending} />
      <NavItem to="/activity" icon={ActivityIcon} label="Staff activity" />
    </>
  );

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col bg-[#0d1124] lg:flex">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-indigo-600/20 to-transparent" />
        <div className="relative px-5 pb-6 pt-6"><Brand /></div>
        <nav className="relative flex-1 space-y-1 px-3" aria-label="Console">{nav}</nav>
        <div className="relative border-t border-white/10 p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500/20 text-xs font-bold uppercase text-indigo-200">
              {session?.email?.[0] ?? "?"}
            </span>
            <p className="min-w-0 flex-1 truncate text-xs text-slate-300" title={session?.email}>{session?.email}</p>
            <button onClick={signOut} title="Sign out" aria-label="Sign out" className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white">
              <LogoutIcon />
            </button>
          </div>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="sticky top-0 z-10 bg-[#0d1124] lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Brand />
          <button onClick={signOut} aria-label="Sign out" className="rounded-md p-2 text-slate-300 hover:bg-white/10">
            <LogoutIcon />
          </button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3" aria-label="Console">{nav}</nav>
      </header>

      <main className="lg:pl-60">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
          <Outlet context={{ overview, refreshOverview }} />
        </div>
      </main>
    </div>
  );
}
